import { useState } from 'react';
import { VendorSetupSocialProof } from '@/contracts/vendor-setup';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, Video, TrendingUp } from 'lucide-react';

interface Props {
  data: VendorSetupSocialProof;
  onChange: (data: VendorSetupSocialProof) => void;
}

export function SetupSocialProofStep({ data, onChange }: Props) {
  const addTestimonial = () => {
    onChange({
      ...data,
      testimonial_videos: [
        ...data.testimonial_videos,
        { title: '', video_url: '', thumbnail_url: '', author_name: '', author_title: '' },
      ],
    });
  };

  const updateTestimonial = (index: number, updates: Partial<typeof data.testimonial_videos[0]>) => {
    const newVideos = [...data.testimonial_videos];
    newVideos[index] = { ...newVideos[index], ...updates };
    onChange({ ...data, testimonial_videos: newVideos });
  };

  const removeTestimonial = (index: number) => {
    const newVideos = data.testimonial_videos.filter((_, i) => i !== index);
    onChange({ ...data, testimonial_videos: newVideos });
  };

  const updateRoiMetric = (tier: 'solo' | 'team' | 'top_team', field: 'before' | 'after', value: number) => {
    const newMetrics = { ...data.roi_metrics };
    if (!newMetrics[tier]) {
      newMetrics[tier] = { before: 0, after: 0 };
    }
    newMetrics[tier]![field] = value;
    onChange({ ...data, roi_metrics: newMetrics });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-2">Social Proof</h2>
        <p className="text-muted-foreground">
          Build trust with testimonials and success metrics from your customers.
        </p>
      </div>

      {/* Testimonial Videos */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Video className="h-5 w-5 text-muted-foreground" />
            <Label className="text-lg font-medium">Video Testimonials</Label>
          </div>
          <Button variant="outline" size="sm" onClick={addTestimonial}>
            <Plus className="h-4 w-4 mr-1" />
            Add Video
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          Add YouTube or Vimeo links to customer testimonial videos
        </p>

        {data.testimonial_videos.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed rounded-lg">
            <Video className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-muted-foreground mt-2">No testimonials yet</p>
            <p className="text-sm text-muted-foreground">Video testimonials help build trust!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {data.testimonial_videos.map((video, index) => (
              <Card key={index}>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Testimonial {index + 1}</span>
                    <Button variant="ghost" size="icon" onClick={() => removeTestimonial(index)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={video.title}
                      onChange={(e) => updateTestimonial(index, { title: e.target.value })}
                      placeholder="Video title"
                    />
                    <Input
                      value={video.video_url}
                      onChange={(e) => updateTestimonial(index, { video_url: e.target.value })}
                      placeholder="YouTube or Vimeo URL"
                    />
                    <Input
                      value={video.author_name || ''}
                      onChange={(e) => updateTestimonial(index, { author_name: e.target.value })}
                      placeholder="Customer name"
                    />
                    <Input
                      value={video.author_title || ''}
                      onChange={(e) => updateTestimonial(index, { author_title: e.target.value })}
                      placeholder="e.g., Top Producer, Austin TX"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* ROI Metrics */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-muted-foreground" />
          <Label className="text-lg font-medium">ROI Metrics (optional)</Label>
        </div>
        <p className="text-sm text-muted-foreground">
          Show before/after results for different agent types. Leave empty if not applicable.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Solo Agent */}
          <Card>
            <CardContent className="pt-4 space-y-3">
              <Label className="font-medium">Solo Agent</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Before</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="number"
                      className="pl-7"
                      value={data.roi_metrics?.solo?.before || ''}
                      onChange={(e) => updateRoiMetric('solo', 'before', Number(e.target.value))}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">After</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="number"
                      className="pl-7"
                      value={data.roi_metrics?.solo?.after || ''}
                      onChange={(e) => updateRoiMetric('solo', 'after', Number(e.target.value))}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Team */}
          <Card>
            <CardContent className="pt-4 space-y-3">
              <Label className="font-medium">Small Team</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Before</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="number"
                      className="pl-7"
                      value={data.roi_metrics?.team?.before || ''}
                      onChange={(e) => updateRoiMetric('team', 'before', Number(e.target.value))}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">After</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="number"
                      className="pl-7"
                      value={data.roi_metrics?.team?.after || ''}
                      onChange={(e) => updateRoiMetric('team', 'after', Number(e.target.value))}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Team */}
          <Card>
            <CardContent className="pt-4 space-y-3">
              <Label className="font-medium">Top Team</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Before</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="number"
                      className="pl-7"
                      value={data.roi_metrics?.top_team?.before || ''}
                      onChange={(e) => updateRoiMetric('top_team', 'before', Number(e.target.value))}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">After</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="number"
                      className="pl-7"
                      value={data.roi_metrics?.top_team?.after || ''}
                      onChange={(e) => updateRoiMetric('top_team', 'after', Number(e.target.value))}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
